// Customer order API contracts (M7, WP7.1; plan §1 "manual orders with promised date",
// §5.4 customer_order / order_line; vault Core Model "Orders and demand", change 7): custom
// and wholesale orders taken in the Admin, with a promised time, four statuses (order,
// payment, fulfilment; carrier status lives per shipment) and their history. The customer's
// name is the only personal data an order carries (POPIA-minimal: no contact details).
//
//   GET  /orders                                  orders by branch / status / promised day
//   POST /orders                                  take an order (idempotency-key header)
//   GET  /orders/:id                              lines, allocations, history, coverage
//   PUT  /orders/:id                              edit (draft or confirmed orders)
//   POST /orders/:id/status                       change order / payment / fulfilment status
//   POST /orders/:id/cancel                       cancel (releases its claims)
//   POST /production/batches/:id/allocations      allocate a planned batch's output to a line
//   DELETE /production/batches/:id/allocations/:allocationId   unallocate (archived)
//
// Permissions: reads `orders.view` or `orders.edit`; writes `orders.edit`; allocating batch
// output `production.plan`. Everything within the caller's branch scope.
import { z } from 'zod';
import { positiveQtyString, qtyString } from '../quantity/index.js';
import { isPlainDate } from '../time/index.js';
import { feasibilityLineSchema } from './feasibility.js';
import { STOCK_PAGE_DEFAULT, STOCK_PAGE_MAX, stockProductRefSchema } from './stock.js';
import { branchRefSchema, lotRefSchema, personRefSchema } from './stock-control.js';

const instant = z.iso.datetime({ offset: true });
const plainDate = z.string().refine(isPlainDate, 'Expected a date, YYYY-MM-DD');
const count = z.int().min(0);
const cents = z.int().min(0).max(100_000_000_000);

/** What kind of order it is. */
export const ORDER_KINDS = ['custom', 'wholesale', 'retail'] as const;
/** Order status (vault change 7). */
export const ORDER_STATUSES = ['draft', 'confirmed', 'completed', 'cancelled'] as const;
/** Payment status. */
export const PAYMENT_STATUSES = [
  'unpaid',
  'partially_paid',
  'paid',
  'partially_refunded',
  'refunded',
] as const;
/** Fulfilment status (carrier status is per shipment, never here). */
export const FULFILMENT_STATUSES = [
  'unfulfilled',
  'in_production',
  'ready',
  'partially_fulfilled',
  'fulfilled',
] as const;
/** One of {@link ORDER_STATUSES}. */
export type OrderStatus = (typeof ORDER_STATUSES)[number];

/**
 * Order status changes a person may make: draft → confirmed; confirmed → completed.
 * Cancelling has its own endpoint (it needs a reason and releases claims); completed and
 * cancelled orders are final.
 */
export function orderStatusChangeAllowed(from: OrderStatus, to: OrderStatus): boolean {
  if (from === to) return true;
  return (from === 'draft' && to === 'confirmed') || (from === 'confirmed' && to === 'completed');
}

/** A line as entered: a catalogue product or a custom item described in words. */
export const orderLineInputSchema = z
  .object({
    /** Keep (and update) this existing line when editing; leave out for a new line. */
    id: z.uuid().optional(),
    product_id: z.uuid().optional(),
    custom_description: z.string().trim().min(1).max(200).optional(),
    /** In the product's base unit (a custom item: in whatever unit the batch making it counts). */
    qty: positiveQtyString,
    /** Per unit, VAT-inclusive, whole cents. */
    unit_price_cents: cents,
    note: z.string().trim().max(1000).optional(),
  })
  .refine((l) => (l.product_id === undefined) !== (l.custom_description === undefined), {
    message: 'A line is either a catalogue product or a custom item, not both',
  });
/** An order line as entered. */
export type OrderLineInput = z.infer<typeof orderLineInputSchema>;

const orderFields = {
  kind: z.enum(ORDER_KINDS),
  customer_name: z.string().trim().min(1).max(120),
  promised_at: instant,
  note: z.string().trim().max(2000).optional(),
  lines: z.array(orderLineInputSchema).min(1).max(100),
};

/** `POST /orders` body. */
export const orderCreateRequestSchema = z.object({
  /** Where it is handed over. Default: the caller's branch, else the default branch. */
  branch_id: z.uuid().optional(),
  ...orderFields,
  /** Default: now. */
  ordered_at: instant.optional(),
  /** Default `confirmed` (a draft is not a commitment: coverage ignores it). */
  order_status: z.enum(['draft', 'confirmed']).optional(),
});
/** `POST /orders` body. */
export type OrderCreateRequest = z.infer<typeof orderCreateRequestSchema>;

/** `PUT /orders/:id` body: the order as it should now be (lines not listed are removed). */
export const orderUpdateRequestSchema = z.object(orderFields);
/** `PUT /orders/:id` body. */
export type OrderUpdateRequest = z.infer<typeof orderUpdateRequestSchema>;

/** `POST /orders/:id/status` body: any of the three statuses. */
export const orderStatusRequestSchema = z
  .object({
    order_status: z.enum(['draft', 'confirmed', 'completed']).optional(),
    payment_status: z.enum(PAYMENT_STATUSES).optional(),
    fulfilment_status: z.enum(FULFILMENT_STATUSES).optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine(
    (b) =>
      b.order_status !== undefined ||
      b.payment_status !== undefined ||
      b.fulfilment_status !== undefined,
    { message: 'Change at least one status' },
  );
/** `POST /orders/:id/status` body. */
export type OrderStatusRequest = z.infer<typeof orderStatusRequestSchema>;

/** `POST /orders/:id/cancel` body. */
export const orderCancelRequestSchema = z.object({ reason: z.string().trim().min(1).max(500) });

/** How well an order is covered in the current feasibility run. */
export const ORDER_COVERAGE = [
  'covered',
  'at_risk',
  'uncovered',
  'not_evaluated',
  /** Not in the run: a draft, finished or cancelled order, or promised after the horizon. */
  'not_checked',
] as const;

/** `GET /orders` query. */
export const orderListQuerySchema = z.object({
  branch_id: z.uuid().optional(),
  /** Order statuses, comma-separated. Default: draft and confirmed. */
  status: z
    .string()
    .transform((v) => v.split(',').filter(Boolean))
    .pipe(z.array(z.enum(ORDER_STATUSES)).min(1))
    .optional(),
  /** Promised on or after this trading day. */
  from: plainDate.optional(),
  /** Promised on or before this trading day. */
  to: plainDate.optional(),
  /** Matches the number or the customer's name. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Kinds, comma-separated (WP7.2). Default: every kind. */
  kind: z
    .string()
    .transform((v) => v.split(',').filter(Boolean))
    .pipe(z.array(z.enum(ORDER_KINDS)).min(1))
    .optional(),
  /** Coverage in the branch's current run, comma-separated (WP7.2). Default: any. */
  coverage: z
    .string()
    .transform((v) => v.split(',').filter(Boolean))
    .pipe(z.array(z.enum(ORDER_COVERAGE)).min(1))
    .optional(),
  limit: z.coerce.number().int().min(1).max(STOCK_PAGE_MAX).default(STOCK_PAGE_DEFAULT),
  cursor: z.string().min(1).max(512).optional(),
});

/** An order in a list. */
export const orderSummarySchema = z.object({
  id: z.uuid(),
  number: z.string(),
  branch: branchRefSchema.nullable(),
  kind: z.enum(ORDER_KINDS),
  customer_name: z.string().nullable(),
  order_status: z.enum(ORDER_STATUSES),
  payment_status: z.enum(PAYMENT_STATUSES),
  fulfilment_status: z.enum(FULFILMENT_STATUSES),
  ordered_at: instant,
  promised_at: instant.nullable(),
  promised_day: plainDate.nullable(),
  total_cents: z.int(),
  line_count: count,
  /** The worst line's coverage in the branch's current run. */
  coverage: z.enum(ORDER_COVERAGE),
  /** Its lines the run finds uncovered, with how much is covered ("8 of 10", D-074). */
  short_lines: z
    .array(z.object({ position: z.int().min(1), covered_qty: qtyString, qty: qtyString }))
    .default([]),
  created_at: instant,
});
/** An order in a list. */
export type OrderSummary = z.infer<typeof orderSummarySchema>;

/** `GET /orders` response. */
export const orderListResponseSchema = z.object({
  items: z.array(orderSummarySchema),
  next_cursor: z.string().nullable(),
  total: count,
});

/** A batch's output claimed for a line (a planned `batch_allocation`). */
export const orderAllocationSchema = z.object({
  id: z.uuid(),
  batch: z.object({
    id: z.uuid(),
    number: z.string(),
    status: z.enum(['planned', 'started', 'completed', 'cancelled']),
    planned_start: instant,
    planned_day: plainDate,
    product: stockProductRefSchema,
  }),
  destination: z.enum(['order_claim', 'immediate_order']),
  qty: qtyString,
});

/** An order line. */
export const orderLineSchema = z.object({
  id: z.uuid(),
  position: z.int().min(1),
  product: stockProductRefSchema.nullable(),
  custom_description: z.string().nullable(),
  qty: qtyString,
  unit_price_cents: z.int(),
  line_total_cents: z.int(),
  note: z.string().nullable(),
  /** Planned batch output claimed for it. */
  allocations: z.array(orderAllocationSchema),
  /** Stock held for it (claims made when a batch completed, D-065). */
  reservations: z.array(
    z.object({
      id: z.uuid(),
      qty: qtyString,
      status: z.enum(['soft', 'claimed', 'released', 'finalised']),
      lot: lotRefSchema.nullable(),
    }),
  ),
  /** Its coverage in the branch's current feasibility run (`null`: not in the run). */
  coverage: feasibilityLineSchema.nullable(),
});
/** An order line. */
export type OrderLine = z.infer<typeof orderLineSchema>;

/** One entry of an order's history. */
export const orderEventSchema = z.object({
  id: z.uuid(),
  kind: z.enum(['created', 'edited', 'status_changed', 'cancelled', 'allocated', 'unallocated']),
  /** `{ field: { from, to } }` or what was allocated. */
  changes: z.record(z.string(), z.unknown()),
  note: z.string().nullable(),
  by: personRefSchema.nullable(),
  occurred_at: instant,
});

/** `GET /orders/:id` response (and the answer to every order write). */
export const orderDetailSchema = orderSummarySchema.extend({
  note: z.string().nullable(),
  lines: z.array(orderLineSchema),
  history: z.array(orderEventSchema),
  created_by: personRefSchema.nullable(),
  cancelled_at: instant.nullable(),
  cancel_reason: z.string().nullable(),
});
/** An order with everything about it. */
export type OrderDetail = z.infer<typeof orderDetailSchema>;

/** `:id` path parameter. */
export const orderIdParamsSchema = z.object({ id: z.uuid() });

/** `POST /production/batches/:id/allocations` body: claim part of the output for a line. */
export const batchAllocateRequestSchema = z.object({
  order_line_id: z.uuid(),
  /** In the batch product's base unit. */
  qty: positiveQtyString,
  /** `immediate_order`: a fresh order joining today's queue. Default `order_claim`. */
  destination: z.enum(['order_claim', 'immediate_order']).optional(),
});
/** `POST /production/batches/:id/allocations` body. */
export type BatchAllocateRequest = z.infer<typeof batchAllocateRequestSchema>;

/** `DELETE /production/batches/:id/allocations/:allocationId` path parameters. */
export const batchAllocationParamsSchema = z.object({ id: z.uuid(), allocationId: z.uuid() });

/** The batch's planned allocations after a change. */
export const batchAllocationsResponseSchema = z.object({
  batch_id: z.uuid(),
  planned_qty: qtyString,
  /** Σ planned allocations. */
  allocated_qty: qtyString,
  allocations: z.array(
    z.object({
      id: z.uuid(),
      destination: z.enum(['shelf', 'order_claim', 'branch_transfer', 'immediate_order']),
      qty: qtyString,
      order_line_id: z.uuid().nullable(),
      order: z.object({ id: z.uuid(), number: z.string() }).nullable(),
    }),
  ),
});
